import {
  Checkbox,
  Dropdown,
  Option,
  Switch,
  Text,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { Controller, type Control } from 'react-hook-form';
import RenderIf from '../RenderIf';
import {
  COMMENT_ROLE_OPTIONS,
  roleLabels,
  type CommentRole,
} from './commentVisibility';

export interface CommentFormValues {
  content: string;
  isPublic: boolean;
  roles: CommentRole[];
  notifyManagers: boolean;
}

interface VisibilityRolesFieldsProps {
  control: Control<CommentFormValues>;
  disabled?: boolean;
}

const useStyles = makeStyles({
  row: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalS),
    '@media (min-width: 900px)': {
      flexDirection: 'row',
      alignItems: 'center',
      ...shorthands.gap(tokens.spacingHorizontalL),
    },
  },
  visibilityGroup: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    ...shorthands.gap(tokens.spacingHorizontalM),
  },
  rolesField: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalXXS),
    width: '190px',
    flexShrink: 0,
  },
  dropdown: {
    minWidth: 'unset',
    width: '190px',
    maxWidth: '190px',
    '& button': {
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      ...shorthands.overflow('hidden'),
    },
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
  },
  notify: {
    '@media (min-width: 900px)': {
      marginLeft: 'auto',
    },
  },
});

export const VisibilityRolesFields = ({
  control,
  disabled,
}: VisibilityRolesFieldsProps) => {
  const classes = useStyles();

  return (
    <div className={classes.row}>
      <div className={classes.visibilityGroup}>
        <Controller
          control={control}
          name="isPublic"
          render={({ field }) => (
            <Switch
              label={field.value ? 'Public' : 'Private'}
              checked={field.value}
              disabled={disabled}
              onChange={(_ev, data) => field.onChange(data.checked)}
            />
          )}
        />

        <Controller
          control={control}
          name="isPublic"
          render={({ field: publicField }) =>
            publicField.value ? (
              <Controller
                control={control}
                name="roles"
                rules={{
                  validate: (value) =>
                    value.length > 0 || 'Select at least one role',
                }}
                render={({ field, fieldState }) => (
                  <div className={classes.rolesField}>
                    <Dropdown
                      multiselect
                      className={classes.dropdown}
                      placeholder="Select roles"
                      disabled={disabled}
                      selectedOptions={field.value}
                      value={roleLabels(field.value)}
                      onOptionSelect={(_ev, data) =>
                        field.onChange(data.selectedOptions as CommentRole[])
                      }
                    >
                      {COMMENT_ROLE_OPTIONS.map((option) => (
                        <Option key={option.value} value={option.value}>
                          {option.label}
                        </Option>
                      ))}
                    </Dropdown>
                    <RenderIf condition={!!fieldState.error}>
                      <Text className={classes.error}>
                        {fieldState.error?.message}
                      </Text>
                    </RenderIf>
                  </div>
                )}
              />
            ) : (
              <></>
            )
          }
        />
      </div>

      <div className={classes.notify}>
        <Controller
          control={control}
          name="notifyManagers"
          render={({ field }) => (
            <Checkbox
              label="Notify managers"
              checked={field.value}
              disabled={disabled}
              onChange={(_ev, data) => field.onChange(!!data.checked)}
            />
          )}
        />
      </div>
    </div>
  );
};
